import { useCallback, useEffect, useRef } from 'react';

import { useAtlassianUserContext } from '../../hooks/use-user-context';

import { useAnalyticsEvents } from './generated/use-analytics-events';
import { type AuthState, type SectionName } from './section-name';

export type UseSectionRevealOptions = {
	/**
	 * IntersectionObserver threshold for "revealed". Defaults to `0.5`
	 * to match the legacy TrackSection behaviour. Footer-style sections
	 * that want a "viewed to bottom" semantic should pass `0` so the
	 * event fires the moment any pixel of the section enters the
	 * viewport.
	 */
	threshold?: number;
	/**
	 * Optional side-effect fired in the same tick as the
	 * `ui.section.revealed.twgMicrositeSection` event. Used by the
	 * Footer to also emit `track.home.viewedToBottom.twgMicrositeHome`
	 * without bloating the hook with section-specific knowledge.
	 *
	 * Receives the active `authState` so callers don't have to pull it
	 * from context themselves.
	 */
	onReveal?: (authState: AuthState) => void;
};

/**
 * Per-section "revealed" tracker. Returns a stable ref-callback that
 * the section component attaches to its own root layout element
 * (`<Box ref={...}>`, `<div ref={...}>`, etc.). Fires
 * `ui.section.revealed.twgMicrositeSection` exactly once per mount,
 * the first time the element crosses the configured visibility
 * threshold.
 *
 * The hook reads auth-state from the surrounding `<UserProvider>` via
 * `useAtlassianUserContext().isAuthed`, so call sites don't have to
 * thread `authState` through props. This keeps section components
 * testable in isolation (just wrap them in a `UserProvider` in tests).
 *
 * Replaces the legacy `<TrackSection>` wrapper component, which
 * introduced an extra `display: block; width: 100%` wrapper div and
 * an a11y-lint-suppressed click handler. The hook approach has zero
 * DOM footprint and lets each section pick its own threshold.
 */
export const useSectionReveal = <T extends Element = HTMLDivElement>(
	sectionName: SectionName,
	options: UseSectionRevealOptions = {},
): React.RefCallback<T> => {
	const { threshold = 0.5, onReveal } = options;
	const { isAuthed } = useAtlassianUserContext();
	const authState: AuthState = isAuthed ? 'loggedIn' : 'loggedOut';
	const { fireEvent } = useAnalyticsEvents();
	const firedRef = useRef(false);
	const observerRef = useRef<IntersectionObserver | null>(null);

	// Hold the latest `onReveal` in a ref so swapping callbacks across
	// renders doesn't tear down + rebuild the IntersectionObserver.
	const onRevealRef = useRef(onReveal);
	useEffect(() => {
		onRevealRef.current = onReveal;
	}, [onReveal]);

	// Hold the latest `authState` in a ref for the same reason.
	const authStateRef = useRef(authState);
	useEffect(() => {
		authStateRef.current = authState;
	}, [authState]);

	return useCallback(
		(node: T | null): void => {
			// Detach + re-arm the previous observer when the host node
			// changes (or unmounts).
			if (observerRef.current) {
				observerRef.current.disconnect();
				observerRef.current = null;
			}
			if (node === null || firedRef.current) {
				return;
			}
			// IntersectionObserver may be missing in older test envs; bail out
			// silently rather than crashing the component.
			if (typeof IntersectionObserver === 'undefined') {
				return;
			}
			const io = new IntersectionObserver(
				(entries) => {
					for (const entry of entries) {
						if (entry.isIntersecting && !firedRef.current) {
							firedRef.current = true;
							const currentAuthState = authStateRef.current;
							fireEvent('ui.section.revealed.twgMicrositeSection', {
								sectionName,
								authState: currentAuthState,
							});
							onRevealRef.current?.(currentAuthState);
							io.disconnect();
							observerRef.current = null;
							break;
						}
					}
				},
				{ threshold },
			);
			io.observe(node);
			observerRef.current = io;
		},
		[fireEvent, sectionName, threshold],
	);
};
